import type { Identifier } from "ra-core";

/**
 * The welcome call: the day after every installation the setter calls the
 * client, asks how it went, writes down everything they say and offers the
 * maintenance plan. One row per order (gula_welcome_calls_summary).
 */
export type WelcomeStatus = "pending" | "done" | "skipped";

export type MaintenanceOffer =
  | "has_it"
  | "monthly"
  | "yearly"
  | "thinking"
  | "declined"
  | "not_offered";

export interface WelcomeCall {
  id: Identifier;
  order_id: Identifier;
  client_id: Identifier | null;
  contact_id: Identifier;
  status: WelcomeStatus;
  assigned_to: Identifier | null;
  due_at: string;
  attempts: number;
  last_attempt_at: string | null;
  done_at: string | null;
  done_by: Identifier | null;
  install_ok: boolean | null;
  installer_rating: number | null;
  satisfaction: number | null;
  has_issue: boolean;
  issue_text: string | null;
  notes: string | null;
  maintenance_offer: MaintenanceOffer | null;
  maintenance_reason: string | null;
  maintenance_recontact_at: string | null;
  maintenance_closed_at: string | null;
  // From the view
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  city: string | null;
  call_slot: string | null;
  product: string | null;
  installed_at: string | null;
  installation_date: string | null;
  sold_by: Identifier | null;
  installer_name: string | null;
  assigned_name: string | null;
  done_by_name: string | null;
  has_maintenance: boolean;
  /** The live maintenance subscription in Stripe, when there is one */
  maintenance_status: string | null;
  maintenance_interval: "month" | "year" | null;
  maintenance_amount: number | null;
  maintenance_next_charge: string | null;
  maintenance_plan: string | null;
  /** Admin's choice: sell the plan or only the welcome (null = from Stripe) */
  plan_override?: "sell" | "welcome_only" | null;
}

/**
 * What the call is about, read from Stripe: just the welcome (the monthly
 * plan is running), a reminder (the yearly plan is paid next year, three
 * weeks before the filter change), or the welcome plus the sale of the plan.
 */
export type MaintenanceState =
  | "none"
  | "monthly"
  | "yearly_scheduled"
  | "yearly"
  | "past_due";

const shortDate = (date: string) =>
  new Date(date).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export const euros = (amount: number | null | undefined) =>
  `${Number(amount ?? 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

export const maintenanceState = (
  call: Pick<
    WelcomeCall,
    "has_maintenance" | "maintenance_status" | "maintenance_interval"
  >,
): MaintenanceState => {
  if (!call.has_maintenance) return "none";
  if (call.maintenance_status === "past_due") return "past_due";
  if (call.maintenance_interval === "year")
    return call.maintenance_status === "trialing"
      ? "yearly_scheduled"
      : "yearly";
  return "monthly";
};

/** Short chip for the list, and the sentence the setter reads in the dialog. */
export const maintenanceSummary = (
  call: WelcomeCall,
): { chip: string; tone: "sell" | "ok" | "warn"; detail: string } => {
  if (call.plan_override === "welcome_only") {
    return {
      chip: "Solo bienvenida",
      tone: "ok",
      detail:
        "Solo la llamada de bienvenida: un admin ha decidido no ofrecerle el plan esta vez.",
    };
  }
  if (call.plan_override === "sell") {
    return {
      chip: "Con venta del plan",
      tone: "sell",
      detail:
        "Ofrécele el plan de mantenimiento (9,99 €/mes o el anual) ahora que está contento.",
    };
  }
  const state = maintenanceState(call);
  const amount = euros(call.maintenance_amount);
  switch (state) {
    case "monthly":
      return {
        chip: "Solo bienvenida · mensual activo",
        tone: "ok",
        detail: `Ya paga el plan mensual (${amount}/mes). No hay nada que vender: recuérdale que los filtros le llegan solos.`,
      };
    case "yearly_scheduled":
      return {
        chip: `Anual: ${amount} se cobra el ${
          call.maintenance_next_charge
            ? shortDate(call.maintenance_next_charge)
            : "año que viene"
        }`,
        tone: "ok",
        detail: `Tiene el plan anual contratado: los ${amount} se le cobran el ${
          call.maintenance_next_charge
            ? shortDate(call.maintenance_next_charge)
            : "año que viene"
        }, tres semanas antes de que le toque el cambio de filtros. Recuérdaselo para que no le pille de sorpresa.`,
      };
    case "yearly":
      return {
        chip: "Solo bienvenida · anual pagado",
        tone: "ok",
        detail: `Ya tiene el plan anual pagado (${amount}/año). Nada que vender.`,
      };
    case "past_due":
      return {
        chip: "Mantenimiento impagado",
        tone: "warn",
        detail:
          "Tiene el plan de mantenimiento pero el último cobro ha fallado. Pídele que revise la tarjeta.",
      };
    default:
      return {
        chip: "Con venta del plan",
        tone: "sell",
        detail:
          "No tiene mantenimiento: ofrécele el plan (9,99 €/mes o el anual) ahora que está contento.",
      };
  }
};

/** What the setter fills in during the call (the RPC takes it as JSON). */
export interface WelcomeForm {
  install_ok: boolean | null;
  installer_rating: number | null;
  satisfaction: number | null;
  has_issue: boolean;
  issue_text: string;
  notes: string;
  maintenance_offer: MaintenanceOffer | "";
  maintenance_reason: string;
  maintenance_recontact_at: string;
}

export const emptyForm = (call: WelcomeCall): WelcomeForm => ({
  install_ok: call.install_ok,
  installer_rating: call.installer_rating,
  satisfaction: call.satisfaction,
  has_issue: call.has_issue,
  issue_text: call.issue_text ?? "",
  notes: call.notes ?? "",
  maintenance_offer:
    call.maintenance_offer ??
    (call.plan_override === "welcome_only"
      ? "not_offered"
      : call.has_maintenance && call.plan_override !== "sell"
        ? "has_it"
        : ""),
  maintenance_reason: call.maintenance_reason ?? "",
  maintenance_recontact_at: call.maintenance_recontact_at ?? "",
});

export const MAINTENANCE_OPTIONS: {
  value: MaintenanceOffer;
  label: string;
  hint?: string;
}[] = [
  {
    value: "monthly",
    label: "Lo quiere mensual",
    hint: "9,99 €/mes · mándale el enlace de pago",
  },
  {
    value: "yearly",
    label: "Lo quiere anual",
    hint: "mándale el enlace de pago",
  },
  {
    value: "thinking",
    label: "Se lo piensa",
    hint: "apunta cuándo volver a llamar",
  },
  { value: "declined", label: "No lo quiere", hint: "apunta el motivo" },
  { value: "not_offered", label: "No se lo he ofrecido" },
];

export const MAINTENANCE_LABEL: Record<MaintenanceOffer, string> = {
  has_it: "Ya lo tiene",
  monthly: "Quiere el mensual",
  yearly: "Quiere el anual",
  thinking: "Se lo piensa",
  declined: "No lo quiere",
  not_offered: "No ofrecido",
};

/** The script, step by step, so every call covers the same ground. */
export const SCRIPT_STEPS = [
  "Preséntate: «Hola, soy … de Gula de Agua. Te llamo para ver qué tal fue la instalación y si está todo bien».",
  "Pregunta por el instalador (puntual, limpio, explicó el equipo) y por el agua: sabor, presión, si todo funciona.",
  "Si hay cualquier pega, apúntala tal cual la cuente: la resolvemos nosotros.",
  "Ofrece el mantenimiento: cambio de filtros y revisión, 9,99 €/mes o el plan anual. Si lo quiere, mándale el enlace de pago por WhatsApp.",
  "Cierra: «Cualquier cosa, nos tienes en el 614 404 301». Guarda la llamada.",
];

/** Missing pieces before the call can be saved as done. */
export const formProblems = (form: WelcomeForm): string[] => {
  const problems: string[] = [];
  if (form.install_ok == null) problems.push("Di si la instalación fue bien");
  if (form.has_issue && !form.issue_text.trim())
    problems.push("Apunta cuál es la incidencia");
  if (!form.maintenance_offer)
    problems.push("Di qué pasa con el mantenimiento");
  if (form.maintenance_offer === "thinking" && !form.maintenance_recontact_at)
    problems.push("Pon la fecha para volver a ofrecerlo");
  return problems;
};

/** The JSON the RPC expects: empty strings become nulls there. */
export const toRpcData = (form: WelcomeForm): Record<string, unknown> => ({
  install_ok: form.install_ok,
  installer_rating: form.installer_rating,
  satisfaction: form.satisfaction,
  has_issue: form.has_issue,
  issue_text: form.issue_text,
  notes: form.notes,
  maintenance_offer: form.maintenance_offer,
  maintenance_reason: form.maintenance_reason,
  maintenance_recontact_at: form.maintenance_recontact_at,
});

/** Due now or overdue: due_at has passed. */
export const isDue = (call: Pick<WelcomeCall, "due_at">, now = new Date()) =>
  new Date(call.due_at).getTime() <= now.getTime();

/**
 * The pending calls one person works on: theirs, plus the unassigned ones.
 * Admins see every pending call.
 */
export const callsFor = (
  calls: WelcomeCall[],
  meId: Identifier | null | undefined,
  isAdmin: boolean,
) =>
  calls
    .filter((call) => call.status === "pending")
    .filter(
      (call) =>
        isAdmin ||
        call.assigned_to == null ||
        String(call.assigned_to) === String(meId),
    )
    // Setters only get the calls to sell the plan; the welcome-only ones
    // (plan already paid, or decided by an admin) are for the admins (5 nov)
    .filter((call) => isAdmin || sellsPlan(call))
    .sort((a, b) => a.due_at.localeCompare(b.due_at));

export const clientName = (call: WelcomeCall) =>
  [call.first_name, call.last_name].filter(Boolean).join(" ") || "Cliente";

/** "Instalado el 29 oct" from the order dates. */
export const installedLabel = (call: WelcomeCall) => {
  const date = call.installed_at ?? call.installation_date;
  if (!date) return null;
  return `Instalado el ${new Date(date).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
  })}`;
};

/** A one-line summary of a finished call for the client sheet. */
export const resultLine = (call: WelcomeCall) => {
  const parts: string[] = [];
  if (call.install_ok != null)
    parts.push(call.install_ok ? "Instalación bien" : "Instalación con pegas");
  if (call.satisfaction != null)
    parts.push(`Satisfacción ${call.satisfaction}/5`);
  if (call.installer_rating != null)
    parts.push(`Instalador ${call.installer_rating}/5`);
  if (call.has_issue) parts.push("Incidencia");
  if (call.maintenance_offer)
    parts.push(
      call.maintenance_closed_at
        ? "Mantenimiento contratado"
        : `Mantenimiento: ${MAINTENANCE_LABEL[call.maintenance_offer].toLowerCase()}`,
    );
  return parts.join(" · ");
};

/** The call includes selling the maintenance plan. */
export const sellsPlan = (call: WelcomeCall) =>
  maintenanceSummary(call).tone === "sell";
